// Offline-first show assets (Cache API): pinned script + SFX bank docs, every voice clip and SFX clip, kept on this laptop.
const CACHE = 'spore-show-v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const open = () => (typeof caches === 'undefined' ? Promise.resolve(null) : caches.open(CACHE).catch(() => null));

export const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
export const audioUrl = (path) => `${API}/professor/audio/${path}`;

const net = { slowMs: 0, lastOk: null };
export const netState = () => ({ online: typeof navigator === 'undefined' || navigator.onLine !== false, slow: net.slowMs > 3000 || ['slow-2g', '2g'].includes(navigator.connection && navigator.connection.effectiveType) });

async function fetchT(url, ms, opts = {}, keepAlive = false) {
  const ac = new AbortController();
  const t0 = performance.now();
  const id = setTimeout(() => ac.abort(), ms);
  if (keepAlive) setTimeout(() => ac.abort(), ms); // keeps covering the body read after the headers arrive
  try {
    const r = await fetch(url, { ...opts, signal: ac.signal });
    net.slowMs = performance.now() - t0;
    return r;
  } finally {
    clearTimeout(id);
  }
}

// a clip on this laptop, else the network (then saved). Resolves an ArrayBuffer and where it came from.
export async function cachedAudio(path, ms = 15000) {
  const c = await open();
  const hit = c && (await c.match(audioUrl(path)));
  if (hit) return { buf: await hit.arrayBuffer(), local: true };
  // the timeout covers the BODY too (a stalled download must never hang the fill); saved straight from the page
  const buf = await withTimeout(fetchT(audioUrl(path), ms, {}, true).then(async (r) => {
    if (!r.ok) throw new Error(String(r.status));
    return r.arrayBuffer();
  }), ms);
  if (c) await c.put(audioUrl(path), new Response(buf.slice(0), { headers: { 'Content-Type': 'audio/mpeg' } })).catch(() => {});
  return { buf, local: false };
}
export async function hasAudio(path) {
  const c = await open();
  return !!(c && (await c.match(audioUrl(path))));
}

// JSON doc (script / sfx bank): the saved copy first; the network only when nothing is saved yet
export async function cachedJson(path, ms = 15000) {
  const c = await open();
  const hit = c && (await c.match(`${API}${path}`));
  if (hit) return { doc: await hit.json(), local: true };
  const doc = await fetchJson(path, ms);
  return { doc, local: false };
}
// network only (background "is there a newer pinned version?"); saves it when it arrives
export async function fetchJson(path, ms = 10000) {
  const r = await fetchT(`${API}${path}`, ms, { cache: 'no-store' });
  if (!r.ok) return null;
  const c = await open();
  if (c) await c.put(`${API}${path}`, r.clone()).catch(() => {});
  return r.json();
}

export async function requestPersist() {
  try {
    return navigator.storage && navigator.storage.persist ? await navigator.storage.persist() : null;
  } catch {
    return false;
  }
}
export async function cacheBytes() {
  try {
    const e = navigator.storage && navigator.storage.estimate ? await navigator.storage.estimate() : null;
    return e ? e.usage : null;
  } catch {
    return null;
  }
}

export const offlineLine = (o) => {
  if (!o) return '';
  const miss = [];
  if (o.voices < o.voicesTotal || !o.voicesTotal) miss.push(`voices ${o.voices}/${o.voicesTotal}`);
  if (o.sfx < o.sfxTotal || !o.sfxTotal) miss.push(`sounds ${o.sfx}/${o.sfxTotal}`);
  if (!o.video) miss.push(`video ${o.videoDl === 'downloading' ? `saving ${o.videoPct}%` : o.videoDl === 'paused' ? `paused ${o.videoPct}%` : o.videoDl === 'error' ? `retrying (${o.videoError || 'error'})` : `missing${o.videoPct ? ` (${o.videoPct}% saved)` : ''}`}`);
  if (!o.app) miss.push('app saving…');
  return o.ready ? `SHOW READY OFFLINE ✓ · voices ${o.voices}/${o.voicesTotal} · sounds ${o.sfx}/${o.sfxTotal} · video ✓ · app ✓` : `NOT READY OFFLINE · ${miss.join(' · ')}`;
};
